import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useGetTransformedApplicationQuery } from "../../Store/api/application/applicationApi";
import {
  GoabCircularProgress,
  GoabCallout,
  GoabPageBlock,
  GoabSpacer,
} from "@abgov/react-components";
import { useSessionManagement } from "../../ServiceUtils/useSessionManagement";
import PortalRoot from "../../PortalRunner/global/PortalRoot";
import { URL_SEARCH_PARAMS } from "../../MPP-Common-SPA/common/constants/urlParams";
import { USER_APPLICATION_SESSION_KEY } from "../../MPP-Common-SPA/utils/globalUtility";
import { useProxyGuard } from "../../PortalRunner/hooks/useProxyGuard";
import { selectCurrentUser } from "../../Store/api/auth/authSelectors";

const Application = () => {
  const [params] = useSearchParams();
  const code = params.get(URL_SEARCH_PARAMS.Code);
  const { data, error, isLoading } = useGetTransformedApplicationQuery(code as string, { skip: !code });
  useProxyGuard();

  const navigate = useNavigate();
  const { getResourceValue } = useSessionManagement();
  const user = useSelector(selectCurrentUser);

  useEffect(() => {
    if (!isLoading && error && 'status' in error && error.status === 404 && code) {
      sessionStorage.removeItem(USER_APPLICATION_SESSION_KEY);
      // SCENARIO: If Proxy User, they must see the 404 page with the MailTo link
      if (user.isProxyUser) {
        // Navigate to the generic 404 page to render the NotFound UI
        navigate("/404", { replace: true }); 
      } else {
        // Standard user behavior remains unchanged
        navigate("/applications", { replace: true });
      }
    }
  }, [isLoading, error, code, navigate, user?.isProxyUser]);

  return (
    <>
      <GoabCircularProgress
        variant="fullscreen"
        visible={isLoading}
        message={getResourceValue({ key: "RESOURCES@global.loading" })}
      />
      {(data || !code) && <PortalRoot />}
      {error && (
        <GoabPageBlock width="704px">
          <GoabSpacer vSpacing="m" />
          <GoabCallout
            type="emergency"
            emphasis="low"
            heading={getResourceValue({
              key: "RESOURCES@global.error.applicationNotLocated.header",
            })}
          >
            {`${getResourceValue({
              key: "RESOURCES@global.error.applicationNotLocated.description",
              params: {
                applicationCode: code,
              },
            })}`}
          </GoabCallout>
        </GoabPageBlock>
      )}
    </>
  );
};

export default Application;
